import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionContext';
import PermissionGuard from '../components/Auth/PermissionGuard';
import { useNavigate } from 'react-router-dom';
import {
  EyeIcon,
  EyeSlashIcon,
  PencilIcon,
  PlusIcon,
  ShieldCheckIcon,
  TrashIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const AdminEmployees = () => {
  const { user, dashboardUser, getToken } = useAuth();
  // Use dashboardUser for dashboard routes
  const currentUser = dashboardUser || user;
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    phone: '',
    is_active: true,
    role_id: null
  });
  const [showPassword, setShowPassword] = useState(false);
  
  // Permission management state
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [roles, setRoles] = useState([]);
  const [allPermissions, setAllPermissions] = useState([]);
  const [employeePermissions, setEmployeePermissions] = useState([]);
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [modulesList, setModulesList] = useState([]);
  const [moduleAccessMap, setModuleAccessMap] = useState({}); // effective map
  const [moduleAccessDraft, setModuleAccessDraft] = useState({}); // editable overrides

  const fetchEmployees = useCallback(async () => {
    try {
      const token = getToken(true); // Dashboard token
      if (!token) {
        console.error('❌ [AdminEmployees] No dashboard token found');
        toast.error('Authentication required. Please login again.');
        navigate('/dashboard/login');
        return;
      }
      
      const API_BASE = process.env.REACT_APP_API_URL || '';
      const url = API_BASE 
        ? `${API_BASE}/api/admin/employees?page=${currentPage}&limit=10&search=${encodeURIComponent(searchTerm)}`
        : `/api/admin/employees?page=${currentPage}&limit=10&search=${encodeURIComponent(searchTerm)}`;
      
      console.log('🔍 [AdminEmployees] Fetching employees from:', url);
      console.log('🔍 [AdminEmployees] Token exists:', !!token);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('🔍 [AdminEmployees] Response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to fetch employees' }));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('🔍 [AdminEmployees] Employees data received:', data);
      
      if (data.success) {
        setEmployees(data.data.employees || []);
        setTotalPages(data.data.pagination?.pages || 1);
      } else {
        throw new Error(data.message || 'Invalid response format');
      }
    } catch (error) {
      console.error('❌ [AdminEmployees] Error fetching employees:', error);
      toast.error(error.message || 'Failed to load employees');
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, getToken, navigate, searchTerm]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = getToken(true); // Dashboard token
      const url = editingEmployee
        ? `/api/admin/employees/${editingEmployee.id}`
        : '/api/admin/employees';
      
      const method = editingEmployee ? 'PUT' : 'POST';
      const body = { ...formData };
      
      // Don't send password if editing and password is empty
      if (editingEmployee && !body.password) {
        delete body.password;
      }

      // Handle role_id - send null if empty string
      if (body.role_id === '' || body.role_id === null) {
        body.role_id = null;
      }

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });

      const data = await response.json();
      if (data.success) {
        toast.success(editingEmployee ? 'Employee updated successfully' : 'Employee created successfully');
        
        // If role_id was set, also update the role
        const employeeId = editingEmployee ? editingEmployee.id : (data.data?.employee?.id || data.data?.id);
        if (body.role_id && employeeId) {
          await handleRoleChange(employeeId, body.role_id);
        }
        
        setShowForm(false);
        setEditingEmployee(null);
        resetForm();
        fetchEmployees();
      } else {
        toast.error(data.message || 'Operation failed');
      }
    } catch (error) {
      console.error('Error saving employee:', error);
      toast.error('Failed to save employee');
    }
  };

  const handleEdit = (employee) => {
    setEditingEmployee(employee);
    setFormData({
      first_name: employee.first_name,
      last_name: employee.last_name,
      email: employee.email,
      password: '',
      phone: employee.phone || '',
      is_active: employee.is_active,
      role_id: employee.role_id || null
    });
    setShowForm(true);
  };

  const fetchEmployeeDetails = async (id) => {
    console.log('🔍🔍🔍 [AdminEmployees] fetchEmployeeDetails CALLED with ID:', id);
    
    try {
      const token = getToken(true); // Dashboard token
      console.log('🔍 [AdminEmployees] Token exists:', !!token);
      
      if (!token) {
        console.error('❌ [AdminEmployees] No token found');
        toast.error('Authentication required. Please login again.');
        navigate('/dashboard/login');
        return;
      }
      
      const API_BASE = process.env.REACT_APP_API_URL || '';
      const url = API_BASE ? `${API_BASE}/api/admin/employees/${id}` : `/api/admin/employees/${id}`;
      
      console.log('🔍 [AdminEmployees] Fetching employee details from:', url);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('🔍 [AdminEmployees] Response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to fetch employee details' }));
        console.error('❌ [AdminEmployees] API Error:', errorData);
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('🔍 [AdminEmployees] Employee data received:', data);
      console.log('🔍 [AdminEmployees] data.success:', data.success);
      console.log('🔍 [AdminEmployees] data.data:', data.data);
      console.log('🔍 [AdminEmployees] data.data.employee:', data.data?.employee);
      
      if (data.success && data.data) {
        console.log('✅ [AdminEmployees] Setting selectedEmployee with:', data.data);
        setSelectedEmployee(data.data);
        console.log('✅ [AdminEmployees] selectedEmployee should now be set');
      } else {
        console.error('❌ [AdminEmployees] Invalid response format');
        throw new Error(data.message || 'Invalid response format');
      }
    } catch (error) {
      console.error('❌ [AdminEmployees] Error fetching employee details:', error);
      toast.error(error.message || 'Failed to load employee details');
    }
  };

  const fetchRoles = useCallback(async () => {
    try {
      console.log('📋 fetchRoles: Starting...');
      const token = getToken(true);
      console.log('📋 fetchRoles: Token exists:', !!token);
      
      const response = await fetch('/api/admin/roles', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      console.log('📋 fetchRoles: Response status:', response.status);
      const data = await response.json();
      console.log('📋 fetchRoles: Response data:', data);
      
      if (data.success) {
        console.log('📋 fetchRoles: Setting roles:', data.data);
        setRoles(data.data || []);
      } else {
        console.error('📋 fetchRoles: Failed - ', data.message);
      }
    } catch (error) {
      console.error('📋 fetchRoles: Error -', error);
    }
  }, [getToken]);

  const fetchAllPermissions = useCallback(async () => {
    try {
      console.log('📋 fetchAllPermissions: Starting...');
      const token = getToken(true);
      console.log('📋 fetchAllPermissions: Token exists:', !!token);
      
      const response = await fetch('/api/admin/permissions', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      console.log('📋 fetchAllPermissions: Response status:', response.status);
      
      if (!response.ok) {
        console.error('❌ fetchAllPermissions: Response not OK:', response.status, response.statusText);
        const errorText = await response.text();
        console.error('Response body:', errorText);
        toast.error('Failed to load permissions');
        return;
      }
      
      const data = await response.json();
      console.log('📋 fetchAllPermissions: Response data:', JSON.stringify(data, null, 2));
      
      if (data.success) {
        // Handle both grouped and flat format
        let permissionsArray = [];
        
        if (data.flat) {
          console.log('📋 fetchAllPermissions: Using flat format with', data.flat.length, 'items');
          permissionsArray = data.flat;
        } else if (Array.isArray(data.data)) {
          console.log('📋 fetchAllPermissions: Using array format with', data.data.length, 'items');
          permissionsArray = data.data;
        } else if (typeof data.data === 'object' && data.data !== null) {
          console.log('📋 fetchAllPermissions: Converting grouped to flat');
          // Convert grouped object to flat array
          const flat = [];
          Object.entries(data.data).forEach(([module, modulePerms]) => {
            console.log(`   Converting module '${module}':`, typeof modulePerms, Array.isArray(modulePerms));
            if (Array.isArray(modulePerms)) {
              flat.push(...modulePerms);
            } else if (typeof modulePerms === 'object') {
              Object.values(modulePerms).forEach(actionPerms => {
                if (Array.isArray(actionPerms)) {
                  flat.push(...actionPerms);
                }
              });
            }
          });
          permissionsArray = flat;
          console.log('   Result:', permissionsArray.length, 'permissions');
        } else {
          console.error('❌ fetchAllPermissions: Unexpected data format:', typeof data.data);
        }
        
        console.log('📋 fetchAllPermissions: Setting permissions array:', permissionsArray.length, 'items');
        if (permissionsArray.length === 0) {
          console.warn('⚠️ fetchAllPermissions: Empty permissions array!');
        } else {
          console.log('📋 Sample permissions:', permissionsArray.slice(0, 3).map(p => `${p.module}:${p.name}`));
        }
        setAllPermissions(permissionsArray);
      } else {
        console.error('❌ fetchAllPermissions: API returned success=false -', data.message);
        toast.error(data.message || 'Failed to load permissions');
      }
    } catch (error) {
      console.error('❌ fetchAllPermissions: Exception -', error);
      console.error(error.stack);
      toast.error('Error loading permissions: ' + error.message);
    }
  }, [getToken]);

  useEffect(() => {
    if (!currentUser) return;
    fetchEmployees();
    fetchRoles();
    fetchAllPermissions();
  }, [currentPage, currentUser, fetchAllPermissions, fetchEmployees, fetchRoles, searchTerm]);

  const fetchModulesList = async () => {
    try {
      const token = getToken(true);
      const response = await fetch('/api/admin/modules', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success && Array.isArray(data.data)) {
        setModulesList(data.data);
      }
    } catch (error) {
      console.error('Error fetching modules:', error);
    }
  };

  const fetchEmployeeModuleAccess = async (employeeId) => {
    try {
      const token = getToken(true);
      const response = await fetch(`/api/admin/employees/${employeeId}/module-access`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setModuleAccessMap(data.data?.effective || {});
        setModuleAccessDraft(data.data?.overrides || {});
        if (Array.isArray(data.data?.modules) && data.data.modules.length > 0) {
          setModulesList(data.data.modules);
        }
      }
    } catch (error) {
      console.error('Error fetching module access:', error);
      setModuleAccessMap({});
      setModuleAccessDraft({});
    }
  };

  const openPermissionsModal = async (employee) => {
    console.log('🔍 Opening permissions modal for employee:', employee);
    setSelectedEmployee(employee);
    setShowPermissionsModal(true);
    setPermissionsLoading(true);
    
    // Fetch roles, permissions, modules if not already loaded
    if (roles.length === 0) {
      console.log('📋 Fetching roles...');
      await fetchRoles();
    }
    if (allPermissions.length === 0) {
      console.log('📋 Fetching all permissions...');
      await fetchAllPermissions();
    }
    if (modulesList.length === 0) {
      await fetchModulesList();
    }
    
    try {
      const token = getToken(true);
      console.log('📋 Fetching employee permissions for ID:', employee.id);
      const response = await fetch(`/api/admin/employees/${employee.id}/permissions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      console.log('📋 Employee permissions response:', data);
      if (data.success) {
        // Handle both old format (data.data.permissions) and new format (data.data)
        const permissions = data.data?.permissions || data.data || [];
        console.log('📋 Setting employee permissions:', permissions);
        setEmployeePermissions(Array.isArray(permissions) ? permissions : []);
      }
    } catch (error) {
      console.error('Error fetching employee permissions:', error);
      toast.error('Failed to load employee permissions');
    } finally {
      // Fetch module access after permissions
      await fetchEmployeeModuleAccess(employee.id);
      setPermissionsLoading(false);
    }
  };

  const handleRoleChange = async (employeeId, roleId) => {
    try {
      const token = getToken(true);
      const response = await fetch(`/api/admin/employees/${employeeId}/role`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ role_id: roleId })
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Role updated successfully');
        
        // Refresh employee permissions after role change
        if (roleId) {
          const permResponse = await fetch(`/api/admin/employees/${employeeId}/permissions`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const permData = await permResponse.json();
          if (permData.success) {
            setEmployeePermissions(permData.data || []);
          }
        } else {
          setEmployeePermissions([]);
        }
        
        fetchEmployees();
      } else {
        toast.error(data.message || 'Failed to update role');
      }
    } catch (error) {
      console.error('Error updating role:', error);
      toast.error('Failed to update role');
    }
  };

  const handlePermissionToggle = async (employeeId, permissionId, isGranted) => {
    try {
      const token = getToken(true);
      
      if (isGranted) {
        // Remove permission (add to revoked list)
        const response = await fetch(`/api/admin/employees/${employeeId}/permissions/${permissionId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        const data = await response.json();
        if (data.success) {
          setEmployeePermissions(prev => prev.filter(p => p.id !== permissionId));
          toast.success('Permission removed');
        } else {
          toast.error(data.message || 'Failed to remove permission');
        }
      } else {
        // Grant permission (add to custom permissions)
        const currentPermIds = employeePermissions.map(p => p.id);
        const newPermIds = [...currentPermIds, permissionId];
        
        const response = await fetch(`/api/admin/employees/${employeeId}/permissions`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ permission_ids: newPermIds })
        });
        
        const data = await response.json();
        if (data.success) {
          // Fetch updated permissions
          const permResponse = await fetch(`/api/admin/employees/${employeeId}/permissions`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const permData = await permResponse.json();
          if (permData.success) {
            setEmployeePermissions(permData.data || []);
          }
          toast.success('Permission granted');
        } else {
          toast.error(data.message || 'Failed to grant permission');
        }
      }
    } catch (error) {
      console.error('Error toggling permission:', error);
      toast.error('Failed to update permission');
    }
  };

  const handleModuleToggle = (moduleKey) => {
    setModuleAccessDraft((prev) => {
      const current = Object.prototype.hasOwnProperty.call(prev, moduleKey)
        ? !!prev[moduleKey]
        : !!moduleAccessMap[moduleKey];
      return { ...prev, [moduleKey]: !current };
    });
  };

  const handleSaveModuleAccess = async (employeeId) => {
    try {
      const token = getToken(true);
      const response = await fetch(`/api/admin/employees/${employeeId}/module-access`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ modules: moduleAccessDraft })
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Module access updated');
        await fetchEmployeeModuleAccess(employeeId);
      } else {
        toast.error(data.message || 'Failed to update module access');
      }
    } catch (error) {
      console.error('Error saving module access:', error);
      toast.error('Failed to update module access');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this employee?')) {
      return;
    }

    try {
      const token = getToken(true); // Dashboard token
      const API_BASE = process.env.REACT_APP_API_URL || '';
      const url = API_BASE ? `${API_BASE}/api/admin/employees/${id}` : `/api/admin/employees/${id}`;
      
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Employee deleted successfully');
        fetchEmployees();
      } else {
        toast.error(data.message || 'Failed to delete employee');
      }
    } catch (error) {
      console.error('Error deleting employee:', error);
      toast.error('Failed to delete employee');
    }
  };

  const resetForm = () => {
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      password: '',
      phone: '',
      is_active: true,
      role_id: null
    });
    setShowPassword(false);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingEmployee(null);
    resetForm();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <PermissionGuard permission="employees.view" module="employees">
      <div className="min-h-screen bg-gray-50 py-4 sm:py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Employees</h1>
              <p className="mt-2 text-gray-600">Manage employee accounts and permissions</p>
            </div>
            <button
              onClick={() => {
                resetForm();
                setShowForm(true);
              }}
              className="flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Add Employee
            </button>
        </div>

        {/* Search */}
        <div className="mb-6">
          <input
            type="text"
            placeholder="Search employees..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            className="w-full max-w-md px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Employees Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="xl:hidden divide-y divide-gray-200">
            {employees.length === 0 ? (
              <div className="px-4 py-12 text-center text-gray-500">
                No employees found
              </div>
            ) : (
              employees.map((employee) => (
                <div key={employee.id} className="p-4 space-y-4">
                  <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <div className="text-base font-semibold text-gray-900">
                        {employee.first_name} {employee.last_name}
                      </div>
                      <div className="text-xs text-gray-500">Employee ID: {employee.id}</div>
                    </div>
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-semibold border ${
                        employee.is_active
                          ? 'bg-green-100 text-green-800 border-green-300'
                          : 'bg-red-100 text-red-800 border-red-300'
                      }`}
                    >
                      {employee.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div>
                      <div className="text-xs font-medium uppercase tracking-wider text-gray-500">Email</div>
                      <div className="mt-1 text-sm text-gray-900 break-all">{employee.email}</div>
                    </div>

                    <div>
                      <div className="text-xs font-medium uppercase tracking-wider text-gray-500">Phone</div>
                      <div className="mt-1 text-sm text-gray-900">{employee.phone || '-'}</div>
                    </div>

                    <div className="sm:col-span-2">
                      <div className="text-xs font-medium uppercase tracking-wider text-gray-500">Role</div>
                      <div className="mt-2">
                        <span className="px-3 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                          {employee.role_display_name || employee.role_name || 'No Role'}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <button
                      onClick={() => openPermissionsModal(employee)}
                      className="inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-3 text-sm font-semibold text-white hover:bg-indigo-700 sm:col-span-2"
                    >
                      <ShieldCheckIcon className="h-4 w-4" />
                      Manage Permissions
                    </button>
                    <button
                      onClick={() => fetchEmployeeDetails(employee.id)}
                      className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-50 px-4 py-3 text-sm font-medium text-blue-700 hover:bg-blue-100"
                    >
                      <EyeIcon className="h-4 w-4" />
                      View Details
                    </button>
                    <button
                      onClick={() => handleEdit(employee)}
                      className="inline-flex items-center justify-center gap-2 rounded-lg bg-slate-100 px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-200"
                    >
                      <PencilIcon className="h-4 w-4" />
                      Edit
                    </button>
                    {hasPermission('employees.manage') && (
                      <button
                        onClick={() => handleDelete(employee.id)}
                        className="inline-flex items-center justify-center gap-2 rounded-lg bg-red-50 px-4 py-3 text-sm font-medium text-red-700 hover:bg-red-100 sm:col-span-2"
                      >
                        <TrashIcon className="h-4 w-4" />
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="hidden xl:block overflow-x-auto overscroll-x-contain">
            <table className="min-w-[1100px] divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Phone
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {employees.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                      No employees found
                    </td>
                  </tr>
                ) : (
                  employees.map((employee) => (
                    <tr key={employee.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {employee.first_name} {employee.last_name}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{employee.email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{employee.phone || '-'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                          {employee.role_display_name || employee.role_name || 'No Role'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            employee.is_active
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {employee.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => openPermissionsModal(employee)}
                            className="flex items-center px-3 py-1 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded gap-1"
                            title="Manage Permissions"
                          >
                            <ShieldCheckIcon className="h-4 w-4" />
                            Permissions
                          </button>
                          <button
                            onClick={() => fetchEmployeeDetails(employee.id)}
                            className="text-blue-600 hover:text-blue-900"
                            title="View Details"
                          >
                            <EyeIcon className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => handleEdit(employee)}
                            className="text-blue-600 hover:text-blue-900"
                            title="Edit"
                          >
                            <PencilIcon className="h-5 w-5" />
                          </button>
                          {hasPermission('employees.manage') && (
                            <button
                              onClick={() => handleDelete(employee.id)}
                              className="text-red-600 hover:text-red-900"
                              title="Delete"
                            >
                              <TrashIcon className="h-5 w-5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-gray-50 px-4 py-3 flex flex-col gap-3 border-t border-gray-200 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Page <span className="font-medium">{currentPage}</span> of{' '}
                    <span className="font-medium">{totalPages}</span>
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Next
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>


      {/* Add/Edit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-gray-900">
                  {editingEmployee ? 'Edit Employee' : 'Add Employee'}
                </h2>
                <button
                  onClick={handleCloseForm}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">First Name</label>
                  <input
                    type="text"
                    required
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Last Name</label>
                  <input
                    type="text"
                    required
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Password {editingEmployee && '(leave empty to keep current)'}
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      required={!editingEmployee}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-2 top-3 text-gray-500 hover:text-gray-700"
                    >
                      {showPassword ? (
                        <EyeSlashIcon className="h-5 w-5" />
                      ) : (
                        <EyeIcon className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Phone</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Role</label>
                  <select
                    value={formData.role_id || ''}
                    onChange={(e) => setFormData({ ...formData, role_id: e.target.value ? parseInt(e.target.value) : null })}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">No Role Assigned</option>
                    {roles.map((role) => (
                      <option key={role.id} value={role.id}>
                        {role.display_name} (Level {role.level})
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    Assign a role to grant hierarchical permissions
                  </p>
                </div>

                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Active</span>
                  </label>
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    type="submit"
                    className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700"
                  >
                    {editingEmployee ? 'Update' : 'Create'}
                  </button>
                  <button
                    type="button"
                    onClick={handleCloseForm}
                    className="flex-1 bg-gray-200 text-gray-800 py-2 px-4 rounded-md hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Employee Details Modal */}
      {(() => {
        console.log('🔍 [AdminEmployees] Rendering modal check - selectedEmployee:', selectedEmployee);
        console.log('🔍 [AdminEmployees] selectedEmployee exists?', !!selectedEmployee);
        if (selectedEmployee) {
          console.log('🔍 [AdminEmployees] selectedEmployee.employee:', selectedEmployee.employee);
          console.log('🔍 [AdminEmployees] selectedEmployee direct props:', {
            first_name: selectedEmployee.first_name,
            last_name: selectedEmployee.last_name,
            email: selectedEmployee.email
          });
        }
        return null;
      })()}
      {selectedEmployee && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-gray-900">Employee Details</h2>
                <button
                  onClick={() => setSelectedEmployee(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              <div className="space-y-6">
                {/* Personal Information */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Personal Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">First Name</p>
                      <p className="font-semibold text-gray-900">{selectedEmployee.employee?.first_name || selectedEmployee.first_name || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Last Name</p>
                      <p className="font-semibold text-gray-900">{selectedEmployee.employee?.last_name || selectedEmployee.last_name || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Email</p>
                      <p className="font-semibold text-gray-900">{selectedEmployee.employee?.email || selectedEmployee.email || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Phone</p>
                      <p className="font-semibold text-gray-900">{selectedEmployee.employee?.phone || selectedEmployee.phone || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Role</p>
                      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                        {selectedEmployee.employee?.role || selectedEmployee.role || 'employee'}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Status</p>
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                        (selectedEmployee.employee?.is_active !== undefined ? selectedEmployee.employee.is_active : selectedEmployee.is_active)
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {(selectedEmployee.employee?.is_active !== undefined ? selectedEmployee.employee.is_active : selectedEmployee.is_active) ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Member Since</p>
                      <p className="font-semibold text-gray-900">
                        {(selectedEmployee.employee?.created_at || selectedEmployee.created_at)
                          ? new Date(selectedEmployee.employee?.created_at || selectedEmployee.created_at).toLocaleDateString()
                          : 'N/A'}
                      </p>
                    </div>
                    {(selectedEmployee.employee?.updated_at || selectedEmployee.updated_at) && (
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Last Updated</p>
                        <p className="font-semibold text-gray-900">
                          {new Date(selectedEmployee.employee?.updated_at || selectedEmployee.updated_at).toLocaleDateString()}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Permission Management Modal */}
      {showPermissionsModal && selectedEmployee && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Manage Permissions</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    {selectedEmployee.first_name} {selectedEmployee.last_name} ({selectedEmployee.email})
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowPermissionsModal(false);
                    setSelectedEmployee(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              {permissionsLoading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Role Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-3">
                      Employee Role
                    </label>
                    <select
                      value={selectedEmployee.role_id || ''}
                      onChange={(e) => {
                        const newRoleId = e.target.value;
                        handleRoleChange(selectedEmployee.id, newRoleId);
                        setSelectedEmployee({ ...selectedEmployee, role_id: newRoleId });
                      }}
                      className="w-full px-4 py-3 text-base border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
                    >
                      <option value="">-- Select Role --</option>
                      {roles.map((role) => (
                        <option key={role.id} value={role.id}>
                          {role.display_name}
                        </option>
                      ))}
                    </select>
                    <p className="mt-2 text-xs text-gray-500">
                      Select a role to view and assign its permissions to this employee.
                    </p>
                  </div>

                  {/* Role Permissions Display */}
                  <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-5">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">
                        All Permissions
                      </h3>
                      {selectedEmployee.role_id && (
                        <span className="px-3 py-1 bg-blue-600 text-white text-sm font-medium rounded-full">
                          {roles.find(r => String(r.id) === String(selectedEmployee.role_id))?.display_name} - {employeePermissions.length} selected
                        </span>
                      )}
                    </div>
                    
                    {allPermissions.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <p>Loading all permissions...</p>
                      </div>
                    ) : (
                      (() => {
                        const groupedPermissions = allPermissions.reduce((acc, perm) => {
                          if (!acc[perm.module]) {
                            acc[perm.module] = [];
                          }
                          acc[perm.module].push(perm);
                          return acc;
                        }, {});

                        return (
                          <div className="space-y-4">
                            {Object.entries(groupedPermissions).map(([module, permissions]) => (
                              <div key={module} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                                <div className="bg-gradient-to-r from-indigo-50 to-blue-50 px-4 py-3 border-b border-gray-200">
                                  <h4 className="font-semibold text-indigo-900 capitalize flex items-center">
                                    <span className="w-2 h-2 bg-indigo-600 rounded-full mr-2"></span>
                                    {module}
                                    <span className="ml-auto text-xs font-normal text-indigo-600">
                                      {permissions.filter(p => employeePermissions.some(ep => ep.id === p.id)).length} / {permissions.length}
                                    </span>
                                  </h4>
                                </div>
                                <div className="p-4">
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    {permissions.map((permission) => {
                                      const isChecked = employeePermissions.some(p => p.id === permission.id);
                                      return (
                                        <label
                                          key={permission.id}
                                          className={`flex items-start space-x-3 p-3 rounded-lg border cursor-pointer hover:shadow-md transition-all ${
                                            isChecked 
                                              ? 'bg-green-50 border-green-300' 
                                              : 'bg-gray-50 border-gray-200 hover:border-gray-300'
                                          }`}
                                        >
                                          <div className="flex-shrink-0 mt-0.5">
                                            <input
                                              type="checkbox"
                                              checked={isChecked}
                                              onChange={() => handlePermissionToggle(selectedEmployee.id, permission.id, isChecked)}
                                              className={`w-4 h-4 border-gray-300 rounded focus:ring-indigo-500 cursor-pointer ${
                                                isChecked ? 'text-green-600' : 'text-gray-400'
                                              }`}
                                            />
                                          </div>
                                          <div className="flex-1 min-w-0">
                                            <p className={`text-sm font-medium ${
                                              isChecked ? 'text-green-900' : 'text-gray-700'
                                            }`}>
                                              {permission.display_name}
                                            </p>
                                            {permission.description && (
                                              <p className="text-xs text-gray-500 mt-1">{permission.description}</p>
                                            )}
                                          </div>
                                        </label>
                                      );
                                    })}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        );
                      })()
                    )}
                  </div>

                  {/* Module Access Overrides */}
                  <div className="bg-purple-50 border-2 border-purple-200 rounded-lg p-5">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">Module Access</h3>
                      <button
                        onClick={() => handleSaveModuleAccess(selectedEmployee.id)}
                        className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm"
                      >
                        Save Module Access
                      </button>
                    </div>

                    {modulesList.length === 0 ? (
                      <p className="text-sm text-gray-600">No modules found.</p>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                        {modulesList.map((mod) => {
                          const effective = Object.prototype.hasOwnProperty.call(moduleAccessDraft, mod)
                            ? !!moduleAccessDraft[mod]
                            : !!moduleAccessMap[mod];
                          return (
                            <label
                              key={mod}
                              className={`flex items-center space-x-3 p-3 rounded-lg border cursor-pointer transition-all ${
                                effective ? 'bg-green-50 border-green-300' : 'bg-gray-50 border-gray-200'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={effective}
                                onChange={() => handleModuleToggle(mod)}
                                className="h-4 w-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                              />
                              <span className="text-sm font-medium text-gray-800 capitalize">{mod}</span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                    <p className="text-xs text-gray-500 mt-3">Overrides apply on top of role defaults.</p>
                  </div>

                  <div className="flex justify-end pt-4 border-t">
                    <button
                      onClick={() => {
                        setShowPermissionsModal(false);
                        setSelectedEmployee(null);
                        fetchEmployees();
                      }}
                      className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium"
                    >
                      Done
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
        </div>
      </div>
    </PermissionGuard>
  );
};

export default AdminEmployees;



